import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const UserSettings = () => {
  const { user, changePassword, logout } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setIsChangingPassword(true);

      const result = await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (result.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Network error. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
    }
  };

  return (
    <div className="user-settings">
      <h3>User Account</h3>
      
      {/* User Info Section */}
      <div className="user-info-section">
        <h4>Account Information</h4>
        <div className="user-info-grid">
          <div className="info-item">
            <span className="label">Username:</span>
            <span className="value">{user?.username}</span>
          </div>
          <div className="info-item">
            <span className="label">Role:</span>
            <span className="value role-admin">{user?.role}</span>
          </div>
          <div className="info-item">
            <span className="label">Last Login:</span>
            <span className="value">
              {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Account Created:</span>
            <span className="value">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="password-section">
        <h4>Change Password</h4>
        <p className="section-description">
          Update your password to keep your account secure. Use a strong password that you haven't used before.
        </p>

        <form onSubmit={handlePasswordSubmit} className="password-form">
          {passwordError && (
            <div className="alert alert-error">
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="alert alert-success">
              {passwordSuccess}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="currentPassword">Current Password:</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              disabled={isChangingPassword}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password:</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              disabled={isChangingPassword}
              autoComplete="new-password"
              minLength="6"
              required
            />
            <small>Must be at least 6 characters long</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password:</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              disabled={isChangingPassword}
              autoComplete="new-password"
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isChangingPassword}
          >
            {isChangingPassword ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Session Management */}
      <div className="session-section">
        <h4>Session Management</h4>
        <p className="section-description">
          Sign out of your account to end your current session.
        </p>
        
        <button 
          className="btn btn-secondary"
          onClick={handleLogout}
        >
          Sign Out
        </button>
      </div>

      <style jsx>{`
        .user-settings {
          padding: 20px;
          max-width: 800px;
        }

        .user-info-section,
        .password-section,
        .session-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .section-description {
          color: #666;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .user-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-top: 15px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: white;
          border-radius: 6px;
        }

        .label {
          font-weight: bold;
          color: #495057;
        }

        .value {
          color: #6c757d;
          font-family: monospace;
        }

        .role-admin {
          background: #d1ecf1;
          color: #0c5460;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .password-form {
          max-width: 400px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
          color: #495057;
        }

        .form-group input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .form-group input:focus {
          outline: none;
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .form-group input:disabled {
          background-color: #e9ecef;
          opacity: 1;
        }

        .form-group small {
          display: block;
          margin-top: 5px;
          color: #6c757d;
          font-size: 12px;
        }

        .alert {
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .alert-error {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .alert-success {
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #5a6268;
        }

        h3 {
          color: #333;
          margin-bottom: 20px;
        }

        h4 {
          color: #495057;
          margin-bottom: 15px;
          font-size: 18px;
        }

        @media (max-width: 768px) {
          .user-info-grid {
            grid-template-columns: 1fr;
          }
          
          .info-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
          }
        }
      `}</style>
    </div>
  );
};

export default UserSettings;